import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Box, Button } from '@/components/ui';
import { EntityIcon, iconTintStyle } from '@/components/ui/icon-picker';
import { cn } from '@/lib/utils';

import { PROJECT_LABELS } from '../projects/move-to-project';
import { ProjectPickerPopover } from '../projects/project-picker-popover';

import { HEADER_CONTROL_BOX } from './meetings-common.components';
import { type Meeting } from './meetings.types';

const CHIP_ICON = 'absolute inset-0 !size-4 transition-opacity duration-200';

export function useEnsureProjectLoaded(projectId: string | null) {
  const { projects } = useStores();

  useEffect(() => {
    if (!projectId) return;
    projects.ensureLoaded(projectId).catch((error) => {
      console.error('Failed to load project', error);
    });
  }, [projectId, projects]);
}

export const MeetingProjectChip = observer(function MeetingProjectChip({
  meeting,
}: {
  meeting: Meeting;
}) {
  const { projects } = useStores();
  const projectId = meeting.project_id ?? null;
  const project = projectId ? projects.getById(projectId) : undefined;
  const isResolving = Boolean(projectId) && !project;

  useEnsureProjectLoaded(projectId);

  const label = projectId
    ? (project?.name ?? 'Loading project')
    : PROJECT_LABELS.add;
  const ariaLabel = projectId
    ? `${PROJECT_LABELS.move}${project ? `, current project: ${project.name}` : ''}`
    : PROJECT_LABELS.add;

  return (
    <ProjectPickerPopover target={{ kind: 'meeting', id: meeting.id }}>
      <Button
        variant="ghost"
        size="sm"
        tooltip={label}
        aria-label={ariaLabel}
        className={cn(
          HEADER_CONTROL_BOX,
          'focus-ring transition-colors duration-200'
        )}
        style={projectId ? iconTintStyle(project?.icon?.color) : undefined}
      >
        <Box className="relative size-4">
          <Icons.folderPlus
            strokeWidth={1.6}
            className={cn(
              CHIP_ICON,
              'text-foreground-muted',
              projectId && 'opacity-0'
            )}
          />
          <EntityIcon
            icon={project?.icon}
            size={16}
            className={cn(
              CHIP_ICON,
              'text-foreground',
              !projectId && 'opacity-0',
              isResolving && 'animate-pulse'
            )}
          />
        </Box>
      </Button>
    </ProjectPickerPopover>
  );
});
